/**
 * TanStack Query hooks for content type management.
 *
 * All content types are site-scoped. Each hook requires a siteSlug parameter.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contentTypesApi, contentFieldsApi, schemaApi } from '../api';
import type {
  CreateContentTypeRequest,
  UpdateContentTypeRequest,
  AddFieldRequest,
  UpdateFieldRequest,
  ReorderFieldsRequest,
} from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const contentTypeKeys = {
  all: (siteSlug: string) => ['sites', siteSlug, 'content-types'] as const,
  lists: (siteSlug: string) => [...contentTypeKeys.all(siteSlug), 'list'] as const,
  list: (siteSlug: string) => [...contentTypeKeys.lists(siteSlug)] as const,
  details: (siteSlug: string) => [...contentTypeKeys.all(siteSlug), 'detail'] as const,
  detail: (siteSlug: string, typeSlug: string) => [...contentTypeKeys.details(siteSlug), typeSlug] as const,
  fields: (siteSlug: string, typeSlug: string) => [...contentTypeKeys.detail(siteSlug, typeSlug), 'fields'] as const,
  schema: (siteSlug: string) => ['sites', siteSlug, 'schema'] as const,
};

// ============================================================================
// Content Type Hooks
// ============================================================================

/** Fetch all content types for a site */
export function useContentTypes(siteSlug: string) {
  return useQuery({
    queryKey: contentTypeKeys.list(siteSlug),
    queryFn: () => contentTypesApi.list(siteSlug),
    enabled: !!siteSlug,
  });
}

/** Fetch a single content type with its fields */
export function useContentType(siteSlug: string, typeSlug: string) {
  return useQuery({
    queryKey: contentTypeKeys.detail(siteSlug, typeSlug),
    queryFn: () => contentTypesApi.get(siteSlug, typeSlug),
    enabled: !!siteSlug && !!typeSlug,
  });
}

/** Create a new content type */
export function useCreateContentType(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateContentTypeRequest) => contentTypesApi.create(siteSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.all(siteSlug) });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

/** Update a content type */
export function useUpdateContentType(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ typeSlug, data }: { typeSlug: string; data: UpdateContentTypeRequest }) =>
      contentTypesApi.update(siteSlug, typeSlug, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.all(siteSlug) });
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.detail(siteSlug, variables.typeSlug),
      });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

/** Delete a content type */
export function useDeleteContentType(siteSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ typeSlug, force = false }: { typeSlug: string; force?: boolean }) =>
      contentTypesApi.delete(siteSlug, typeSlug, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.all(siteSlug) });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

// ============================================================================
// Field Hooks
// ============================================================================

/** Fetch fields for a content type */
export function useContentFields(siteSlug: string, typeSlug: string) {
  return useQuery({
    queryKey: contentTypeKeys.fields(siteSlug, typeSlug),
    queryFn: () => contentFieldsApi.list(siteSlug, typeSlug),
    enabled: !!siteSlug && !!typeSlug,
  });
}

/** Add a field to a content type */
export function useAddField(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddFieldRequest) => contentFieldsApi.add(siteSlug, typeSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.detail(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.fields(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

/** Update a field */
export function useUpdateField(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fieldSlug, data }: { fieldSlug: string; data: UpdateFieldRequest }) =>
      contentFieldsApi.update(siteSlug, typeSlug, fieldSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.detail(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.fields(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

/** Delete a field */
export function useDeleteField(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fieldSlug: string) => contentFieldsApi.delete(siteSlug, typeSlug, fieldSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.detail(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.fields(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({ queryKey: contentTypeKeys.schema(siteSlug) });
    },
  });
}

/** Reorder fields in a content type */
export function useReorderFields(siteSlug: string, typeSlug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderFieldsRequest) => contentFieldsApi.reorder(siteSlug, typeSlug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.detail(siteSlug, typeSlug),
      });
      queryClient.invalidateQueries({
        queryKey: contentTypeKeys.fields(siteSlug, typeSlug),
      });
    },
  });
}

// ============================================================================
// Schema Hooks
// ============================================================================

/** Fetch full site schema */
export function useSiteSchema(siteSlug: string) {
  return useQuery({
    queryKey: contentTypeKeys.schema(siteSlug),
    queryFn: () => schemaApi.get(siteSlug),
    enabled: !!siteSlug,
  });
}
